import { isComplianceSubdomain } from './getSubdomain';
import { EU27 } from '../pages/CreateShipment/constants';
import { convertAmountToEur } from './currencyConversion';
import { userLogin, hasAdminAccess } from '../services/hasAdminAccess';

/**
 * TEMPORARY toggle — determines whether EU and US CPSC compliance fields
 * are required at shipment creation / placement time.
 * When false, compliance fields and product ID are optional during create/place.
 * After shipment is placed (Admin edit/unprocessed views), they remain required.
 * Revert to true when required-at-create is re-enabled.
 */
export const COMPLIANCE_REQUIRED_AT_CREATE = false; // TEMPORARY — revert when required-at-create is re-enabled

/**
 * Checks if current page is an admin edit or history view of an already placed shipment.
 */
export function isPlacedShipment(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname;
  return (
    path.includes('/shipment/admin/edit') ||
    path.includes('/shipment/admin/unprocessed/edit') ||
    path.includes('/shipment/history')
  );
}

/**
 * Determines whether EU and US CPSC compliance fields are required.
 * - At shipment create flow, compliance fields are optional when COMPLIANCE_REQUIRED_AT_CREATE is false.
 * - After shipment is placed (isPlacedShipment() === true or isPostPlaced === true), compliance
 *   is enforced per the standard rules.
 * - When a user is logged in (userLogin() === true or hasAdminAccess() === true),
 *   compliance fields are bypassed (optional).
 */
export function isComplianceRequired(explicitIsUserLogin?: boolean, isPostPlaced?: boolean): boolean {
  const placed = typeof isPostPlaced === 'boolean' ? isPostPlaced : isPlacedShipment();
  if (!placed && !COMPLIANCE_REQUIRED_AT_CREATE) {
    return false;
  }
  if (typeof explicitIsUserLogin === 'boolean') {
    return !explicitIsUserLogin;
  }
  try {
    const isPortalUser = userLogin();
    const isAdmin = hasAdminAccess();
    return !(isPortalUser || isAdmin);
  } catch (err) {
    return true;
  }
}

/**
 * Checks if a country name matches any EU-27 member state (case-insensitive).
 */
export function isEUCountry(countryName?: string | null): boolean {
  if (!countryName || typeof countryName !== 'string') return false;
  const clean = countryName.trim().toLowerCase();
  return EU27.some(eu => eu.toLowerCase() === clean);
}

/**
 * Determines whether the VAT field is applicable for the shipment payload.
 * VAT is only meaningful for EU destination + B2B + Parcel (non-document) on compliance subdomains.
 */
export function isVatPayloadApplicable(params: {
  destCountry?: any;
  shipmentMode?: string | null;
  isDocument?: boolean;
  shipmentType?: string | null;
  customHostname?: string;
}): boolean {
  if (!isComplianceSubdomain(params.customHostname)) return false;
  const isDoc = params.isDocument === true || (params.shipmentType || '').toLowerCase() === 'document';
  if (isDoc) return false;
  const isB2B = (params.shipmentMode || '').trim().toLowerCase() === 'b2b';
  if (!isB2B) return false;
  const countryName =
    typeof params.destCountry === 'object' && params.destCountry !== null
      ? (params.destCountry as any)?.name
      : params.destCountry;
  return isEUCountry(countryName);
}

/**
 * Resolves the total shipment value following the priority order:
 * 1. Direct shipment value from "Shipment Value & Weight" section (if available and > 0)
 * 2. Fallback: Computed sum of (quantity * unit_value) across all boxes and products
 */
export function resolveShipmentValue(boxes: any[] = [], directShipmentValue?: number | null): number {
  if (
    directShipmentValue !== undefined &&
    directShipmentValue !== null &&
    !isNaN(directShipmentValue) &&
    directShipmentValue > 0
  ) {
    return Number(directShipmentValue);
  }

  let computedSum = 0;
  if (Array.isArray(boxes)) {
    boxes.forEach(box => {
      if (box && Array.isArray(box.products)) {
        box.products.forEach((p: any) => {
          const qty = Number(p.quantity ?? p.qty ?? 0);
          const val = Number(p.unitValue ?? p.value ?? 0);
          if (!isNaN(qty) && !isNaN(val) && qty > 0 && val > 0) {
            computedSum += qty * val;
          }
        });
      }
    });
  }

  return Number(computedSum.toFixed(2));
}

/**
 * Resolves total shipment value and converts it into EUR using the conversion rate helper.
 * Optionally accepts a live/custom rate (e.g. from useEurExchangeRate hook).
 */
export function resolveShipmentValueInEur(
  boxes: any[] = [],
  currency = 'EUR',
  directShipmentValue?: number | null,
  customRate?: number | null,
): number {
  const value = resolveShipmentValue(boxes, directShipmentValue);
  return convertAmountToEur(value, currency, customRate);
}

/**
 * Evaluates EU compliance rules for Product Identification & EU Compliance fields:
 * - EU + B2C + value <= €150: Visible and REQUIRED (Merchant ID & Manufacturer part no.)
 * - EU + B2B: Visible and OPTIONAL (none required)
 * - Otherwise: Hidden
 */
export function getEuComplianceRule(
  arg1?:
    | {
        destCountry?: string | null;
        shipmentMode?: string | null;
        shipmentValueEur: number;
        isUserLogin?: boolean;
        isPostPlaced?: boolean;
        purposeOfShipment?: string | null;
        shipmentType?: string | null;
        isDocument?: boolean;
        customHostname?: string;
      }
    | string
    | null,
  arg2?: string | null,
  arg3?: number,
  arg4?: boolean,
  arg5?: boolean,
  arg6?: string | null,
  arg7?: string | boolean | null,
  arg8?: string,
) {
  let destCountry: string | null | undefined;
  let shipmentMode: string | null | undefined;
  let shipmentValueEur = 0;
  let explicitIsUserLogin: boolean | undefined = undefined;
  let isPostPlaced: boolean | undefined = undefined;
  let purposeOfShipment: string | null | undefined = undefined;
  let isDoc = false;
  let customHostname: string | undefined = undefined;

  if (typeof arg1 === 'object' && arg1 !== null) {
    destCountry = arg1.destCountry;
    shipmentMode = arg1.shipmentMode;
    shipmentValueEur = arg1.shipmentValueEur || 0;
    explicitIsUserLogin = arg1.isUserLogin;
    isPostPlaced = arg1.isPostPlaced;
    purposeOfShipment = arg1.purposeOfShipment;
    isDoc =
      arg1.isDocument === true ||
      (typeof arg1.shipmentType === 'string' && arg1.shipmentType.trim().toLowerCase() === 'document');
    customHostname = arg1.customHostname;
  } else {
    destCountry = arg1;
    shipmentMode = arg2;
    shipmentValueEur = arg3 || 0;
    explicitIsUserLogin = arg4;
    isPostPlaced = arg5;
    purposeOfShipment = arg6;
    if (typeof arg7 === 'boolean') {
      isDoc = arg7;
    } else if (typeof arg7 === 'string') {
      isDoc = arg7.trim().toLowerCase() === 'document';
    }
    customHostname = arg8;
  }

  if (!isComplianceSubdomain(customHostname)) {
    return {
      isEU: false,
      isB2C: false,
      isEcom: false,
      isB2COrEcom: false,
      isB2B: false,
      isUnderOrEqual150: false,
      isVisible: false,
      isRequired: false,
      showEuComplianceFields: false,
      requireEuComplianceFields: false,
    };
  }

  const isEU = isEUCountry(destCountry);
  const mode = (shipmentMode || '').trim().toLowerCase();
  const isB2C = mode === 'b2c';
  const isEcom = mode === 'ecom';
  const isB2B = mode === 'b2b';
  const isC2C = mode === 'c2c';
  const isB2COrEcom = isB2C || isEcom;
  const isUnderOrEqual150 = shipmentValueEur <= 150;

  const cleanPurpose = (purposeOfShipment || '').trim().toLowerCase();
  const isGift = cleanPurpose === 'gift';
  const isSample = cleanPurpose === 'sample';
  const isGiftOrSample = isGift || isSample;
  const isSale = cleanPurpose === 'sale' || cleanPurpose === 'for_sale';

  // EU Compliance fields (M-PID, Barcode S-PID, NS-PID) are visible for EU destinations
  // (unless shipment type is Document or non-gated subdomain) in:
  // 1. (B2C or ECOM) && (Gift or Sample)  [NEW]
  // 2. C2C && Sale                        [NEW]
  // 3. (B2C or ECOM) && Sale              [EXISTING: both <= 150 and > 150]
  // Note: B2B is hidden (per earlier rule). C2C + Gift/Sample is hidden.
  // EU Compliance fields (M-PID, Barcode S-PID, NS-PID) are visible for EU destinations
  // (unless shipment type is Document or non-gated subdomain) in:
  // 1. (B2C or ECOM) (Gift, Sample, Sale, or default)  [NEW: Gift/Sample visible]
  // 2. C2C && Sale                                     [NEW: C2C Sale visible]
  // Note: B2B is hidden (per earlier rule). C2C + Gift/Sample is hidden.
  const isVisible = !isDoc && isEU && (isB2COrEcom || (isC2C && isSale));

  // Required ONLY for EU + (B2C or ECOM) + Sale + goods total <= €150, when visible and compliance required.
  // Newly visible cases (B2C/ECOM + Gift/Sample, C2C + Sale) and goods total > €150 are NOT required.
  const complianceRequired = isComplianceRequired(explicitIsUserLogin, isPostPlaced);
  const isRequired = isVisible && complianceRequired && isB2COrEcom && !isGiftOrSample && isUnderOrEqual150;

  return {
    isEU,
    isB2C,
    isEcom,
    isB2COrEcom,
    isB2B,
    isC2C,
    isUnderOrEqual150,
    isVisible,
    isRequired,
    showEuComplianceFields: isVisible,
    requireEuComplianceFields: isRequired,
  };
}
